import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  UserPlus,
  Users,
  Edit,
  Trash2,
  X,
  Save,
  MessageCircle,
  Send,
  Stethoscope,
  Globe,
  MapPin
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const ReferralDentistsManagementPage = () => {
  const navigate = useNavigate();
  const { practice } = useAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('dentalToken');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dentists, setDentists] = useState([]);
  const [showAddDentist, setShowAddDentist] = useState(false);
  const [showEditDentist, setShowEditDentist] = useState(false);
  const [editingDentist, setEditingDentist] = useState(null);
  
  // Dr. G chatbot state
  const [showAskDan, setShowAskDan] = useState(false);
  const [danMessages, setDanMessages] = useState([]);
  const [danInput, setDanInput] = useState('');
  const [danLoading, setDanLoading] = useState(false);
  
  const [newDentist, setNewDentist] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialty: '',
    website: '',
    address: ''
  });

  const specialties = [
    'General Dentistry',
    'Orthodontics',
    'Periodontics',
    'Endodontics',
    'Oral Surgery',
    'Oral Pathology',
    'Prosthodontics',
    'Pediatric Dentistry',
    'Medical',
    'Other'
  ];

  useEffect(() => {
    loadDentists();
  }, []);

  const loadDentists = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/referral-dentists`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setDentists(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load referral dentists:', error);
      toast({
        title: "Error",
        description: "Failed to load referral dentists",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddDentist = async () => {
    if (!newDentist.firstName || !newDentist.lastName || !newDentist.specialty) {
      toast({
        title: "Validation Error",
        description: "Please fill in First Name, Last Name, and Specialty (required fields)",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/referral-dentists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newDentist)
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: "Success",
          description: "Referral dentist added successfully",
        });
        setNewDentist({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          specialty: '',
          website: '',
          address: ''
        });
        setShowAddDentist(false);
        loadDentists();
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to add referral dentist",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error adding referral dentist:', error);
      toast({
        title: "Error",
        description: "Failed to add referral dentist",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditDentist = (dentist) => {
    console.log('🔧 Edit clicked for dentist:', dentist);
    setEditingDentist({...dentist});
    setShowEditDentist(true);
  };

  const handleUpdateDentist = async () => {
    if (!editingDentist.firstName || !editingDentist.lastName || !editingDentist.specialty) {
      toast({
        title: "Validation Error",
        description: "Please fill in First Name, Last Name, and Specialty (required fields)",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/referral-dentists/${editingDentist.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editingDentist)
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: "Success",
          description: "Referral dentist updated successfully",
        });
        setShowEditDentist(false);
        setEditingDentist(null);
        loadDentists();
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to update referral dentist",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error updating referral dentist:', error);
      toast({
        title: "Error",
        description: "Failed to update referral dentist",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDentist = async (id) => {
    if (!window.confirm('Are you sure you want to delete this referral dentist?')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/referral-dentists/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: "Success",
          description: "Referral dentist deleted successfully",
        });
        loadDentists();
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to delete referral dentist",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error deleting referral dentist:', error);
      toast({
        title: "Error",
        description: "Failed to delete referral dentist",
        variant: "destructive",
      });
    }
  };

  const handleAskDan = async () => {
    if (!danInput.trim()) return;

    const currentMessage = danInput.trim();
    const userMessage = { role: 'user', content: currentMessage };
    const updatedHistory = [...danMessages, userMessage];
    
    setDanMessages(updatedHistory);
    setDanInput('');
    setDanLoading(true);

    try {
      // Use the same Dr. G endpoint as Referral Management
      const response = await fetch(`${backendUrl}/api/ask-dan/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: currentMessage,
          conversationHistory: danMessages
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.response) {
        setDanMessages([...updatedHistory, { role: 'assistant', content: data.response }]);
      } else {
        throw new Error('Invalid response from AI');
      }
    } catch (error) {
      console.error('Ask Dr. G error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to get response from Dr. G",
        variant: "destructive",
      });
      // Remove the user message if request failed
      setDanMessages(danMessages);
    } finally {
      setDanLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Stethoscope className="h-8 w-8 text-blue-600" />
                Manage Referral Dentists
              </h1>
              <p className="text-gray-600 mt-1">Manage dentists you refer patients to</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={() => setShowAskDan(!showAskDan)}
              className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
            >
              <MessageCircle className="h-4 w-4" />
              {showAskDan ? 'Hide' : 'Ask'} Dr. G
            </Button>
            <Button
              onClick={() => setShowAddDentist(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <UserPlus className="h-4 w-4" />
              Add Referral Dentist
            </Button>
          </div>
        </div>

        {/* Dr. G Chat Widget */}
        {showAskDan && (
          <Card className="mb-6 border-purple-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_dental-dashboard-3/artifacts/a73bkdcb_a-cartoon-style-illustration-of-a-friend_BHWWQ3RORgyazFo06PAsDw_NyHovGrkR5m-Yi-_Gwdm5Q.png"
                    alt="Dr. G" 
                    className="h-8 w-8 rounded-full bg-white" 
                  />
                  Ask Dr. G About Referral Specialists
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAskDan(false);
                    setDanMessages([]);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
                {danMessages.length === 0 && (
                  <p className="text-gray-500 text-center py-8">
                    Ask Dr. G for help finding the right specialist or building your referral network
                  </p>
                )}
                {danMessages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-lg ${
                      m.role === 'user' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Ask about specialists, referral networks, etc..."
                  value={danInput}
                  onChange={(e) => setDanInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAskDan()}
                />
                <Button onClick={handleAskDan} disabled={danLoading}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add Dentist Modal */}
        {showAddDentist && (
          <Card className="mb-6 border-blue-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-center justify-between">
                <CardTitle>Add New Referral Dentist</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddDentist(false);
                    setNewDentist({
                      firstName: '',
                      lastName: '',
                      email: '',
                      phone: '',
                      specialty: '',
                      website: '',
                      address: ''
                    });
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={newDentist.firstName}
                    onChange={(e) => setNewDentist({...newDentist, firstName: e.target.value})}
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={newDentist.lastName}
                    onChange={(e) => setNewDentist({...newDentist, lastName: e.target.value})}
                    placeholder="Enter last name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={newDentist.email}
                    onChange={(e) => setNewDentist({...newDentist, email: e.target.value})}
                    placeholder="email@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1 italic">Note: Email required if you want to email referrals to this dentist</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <Input
                    value={newDentist.phone}
                    onChange={(e) => setNewDentist({...newDentist, phone: e.target.value})}
                    placeholder="(123) 456-7890"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    Specialty <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={newDentist.specialty}
                    onValueChange={(value) => setNewDentist({...newDentist, specialty: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      {specialties.map(spec => (
                        <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Website <span className="text-gray-400 text-xs">(optional, but suggested)</span>
                  </label>
                  <Input
                    value={newDentist.website}
                    onChange={(e) => setNewDentist({...newDentist, website: e.target.value})}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Address <span className="text-gray-400 text-xs">(optional, but suggested)</span>
                  </label>
                  <Textarea
                    value={newDentist.address}
                    onChange={(e) => setNewDentist({...newDentist, address: e.target.value})}
                    placeholder="123 Main St, City, State ZIP"
                    rows={2}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowAddDentist(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddDentist}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Referral Dentist'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Dentist Modal - Using Dialog for proper overlay */}
        <Dialog open={showEditDentist && !!editingDentist} onOpenChange={(open) => {
          if (!open) {
            setShowEditDentist(false);
            setEditingDentist(null);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Referral Dentist</DialogTitle>
            </DialogHeader>
            {editingDentist && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={editingDentist.firstName}
                      onChange={(e) => setEditingDentist({...editingDentist, firstName: e.target.value})}
                      placeholder="Enter first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={editingDentist.lastName}
                      onChange={(e) => setEditingDentist({...editingDentist, lastName: e.target.value})}
                      placeholder="Enter last name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Email
                    </label>
                    <Input
                      type="email"
                      value={editingDentist.email || ''}
                      onChange={(e) => setEditingDentist({...editingDentist, email: e.target.value})}
                      placeholder="email@example.com"
                    />
                    <p className="text-xs text-gray-500 mt-1 italic">Note: Email required if you want to email referrals to this dentist</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <Input
                      value={editingDentist.phone || ''}
                      onChange={(e) => setEditingDentist({...editingDentist, phone: e.target.value})}
                      placeholder="(123) 456-7890"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">
                      Specialty <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={editingDentist.specialty}
                      onValueChange={(value) => setEditingDentist({...editingDentist, specialty: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select specialty" />
                      </SelectTrigger>
                      <SelectContent>
                        {specialties.map(spec => (
                          <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Website <span className="text-gray-400 text-xs">(optional, but suggested)</span>
                    </label>
                    <Input
                      value={editingDentist.website || ''}
                      onChange={(e) => setEditingDentist({...editingDentist, website: e.target.value})}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Address <span className="text-gray-400 text-xs">(optional, but suggested)</span>
                    </label>
                    <Textarea
                      value={editingDentist.address || ''}
                      onChange={(e) => setEditingDentist({...editingDentist, address: e.target.value})}
                      placeholder="123 Main St, City, State ZIP"
                      rows={2}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowEditDentist(false);
                      setEditingDentist(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateDentist}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Update Referral Dentist'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dentists List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Referral Dentists ({dentists.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : dentists.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Stethoscope className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No referral dentists added yet</p>
                <p className="text-sm">Click "Add Referral Dentist" to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {dentists.map((dentist) => (
                  <div
                    key={dentist.id}
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">
                          Dr. {dentist.firstName} {dentist.lastName}
                        </h3>
                        <p className="text-blue-600 font-medium flex items-center gap-2 mt-1">
                          <Stethoscope className="h-4 w-4" />
                          {dentist.specialty}
                        </p>
                        <div className="mt-2 space-y-1 text-sm text-gray-600">
                          <p>📧 {dentist.email}</p>
                          {dentist.phone && <p>📱 {dentist.phone}</p>}
                          {dentist.website && (
                            <p className="flex items-center gap-1">
                              <Globe className="h-3 w-3" />
                              <a href={dentist.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                {dentist.website}
                              </a>
                            </p>
                          )}
                          {dentist.address && (
                            <p className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {dentist.address}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditDentist(dentist)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`edit-dentist-${dentist.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDentist(dentist.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReferralDentistsManagementPage;
